import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AppHeader } from '@/components/layout/AppHeader';
import { 
  Tag,
  Calendar,
  Heart,
  Bookmark,
  Share2,
  Play
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Tool, ToolInteractions } from '@/types/tool';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ToolHeaderProps {
  tool: Tool;
  interactions: ToolInteractions;
  prefix: string;
  onVote: (voteType: 'upvote' | 'downvote') => void;
  onBookmark: () => void;
  onShare: () => void;
  onNavigateToConfig: () => void;
}

export const ToolHeader: React.FC<ToolHeaderProps> = ({
  tool,
  interactions,
  prefix,
  onVote,
  onBookmark,
  onShare,
  onNavigateToConfig
}) => {
  const { user } = useAuth();
  const { lang } = useParams<{ lang?: string }>();
  const authPrefix = `/${lang || 'en'}`;
  const { t } = useTranslation();
  return (
    <AppHeader
      title={tool.name}
      subtitle={tool.description || ''}
      breadcrumbs={[{ label: tool.name }]}
      size="compact"
      showChips={false}
    >
      <div className="flex items-center gap-6 mb-6 text-sm sm:text-base">
        {tool.category && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Tag className="w-4 h-4" />
            <span>{tool.category.name}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>Created {formatDistanceToNow(new Date(tool.created_at), { addSuffix: true })}</span>
        </div>
      </div>

      {tool.tags && tool.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          {tool.tags.map((tagWrapper, index) => (
            <Badge 
              key={index} 
              className="bg-secondary text-foreground border-border hover:bg-primary/10 hover:text-primary"
            >
              {tagWrapper.tags.name}
            </Badge>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <TooltipProvider>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <Tooltip>
              <TooltipTrigger asChild>
                {user ? (
                  <Button
                    variant={interactions.hasVoted && interactions.userVoteType === 'upvote' ? "secondary" : "outline"}
                    onClick={() => onVote('upvote')}
                    className="gap-2"
                  >
                    <Heart className={`w-4 h-4 ${interactions.hasVoted && interactions.userVoteType === 'upvote' ? 'fill-current' : ''}`} />
                    {tool.upvotes_count}
                  </Button>
                ) : (
                  <div className="inline-block">
                    <Button
                      variant="outline"
                      className="gap-2 opacity-50 cursor-not-allowed"
                      disabled
                    >
                      <Heart className="w-4 h-4" />
                      {tool.upvotes_count}
                    </Button>
                  </div>
                )}
              </TooltipTrigger>
              <TooltipContent 
                side="bottom" 
                sideOffset={8}
                className="px-4 py-3 text-sm max-w-xs"
              >
                {user ? (
                  <p>Vote for this tool</p>
                ) : (
                  <div>
                    <p>Sign in to vote for this tool</p>
                    <a 
                      href={`${authPrefix}/auth?tab=signup`}
                      className="text-primary hover:underline font-medium"
                    >
                      Create an account →
                    </a>
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                {user ? (
                  <Button
                    variant="outline"
                    onClick={onBookmark}
                    className="gap-2"
                  >
                    <Bookmark className={`w-4 h-4 ${interactions.isBookmarked ? 'fill-current' : ''}`} />
                    {interactions.isBookmarked ? 'Bookmarked' : 'Bookmark'}
                  </Button>
                ) : (
                  <div className="inline-block">
                    <Button
                      variant="outline"
                      className="gap-2 opacity-50 cursor-not-allowed"
                      disabled
                    >
                      <Bookmark className="w-4 h-4" />
                      {t('generic.bookmark')}
                    </Button>
                  </div>
                )}
              </TooltipTrigger>
              <TooltipContent 
                side="bottom" 
                sideOffset={8}
                className="px-4 py-3 text-sm max-w-xs"
              >
                {user ? (
                  <p>Bookmark this tool</p>
                ) : (
                  <div>
                    <p>Sign in to bookmark this tool</p>
                    <a 
                      href={`${authPrefix}/auth?tab=signup`}
                      className="text-primary hover:underline font-medium"
                    >
                      Create an account →
                    </a>
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
            
            <Button variant="outline" className="gap-2" onClick={onShare}>
              <Share2 className="w-4 h-4" />
              {t('generic.share')}
            </Button>
          </div>

          <Button 
            onClick={onNavigateToConfig}
            size="lg"
            variant="gradient"
            className="w-full sm:w-auto gap-3 px-8 py-3 text-lg font-semibold shadow-lg"
          >
            <Play className="w-5 h-5" />
            {t('tool.header.useThisTool')}
          </Button>
        </div>
      </TooltipProvider>
    </AppHeader>
  );
}; 